'use client';

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { interactionResponse } from '@zillow/yield-callback';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { type ListingSearchMode, useListingSearchState } from './useListingSearchState';
import { defaultModalOptions, type OpenAllFiltersOptions, useAllFiltersModal } from './AllFiltersModalProvider';

const AllFiltersModal = dynamic(() => import('./AllFiltersModal'), { ssr: false });

type SearchFiltersContextValue = {
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  applyFilters: (patch: Partial<FilterState>, options?: { immediate?: boolean }) => void;
  area: AreaResponse | AutocompleteResult | null;
  setArea: (area: AutocompleteResult) => void;
  submit: () => void;
  searchArea: (area: AutocompleteResult) => void;
  isPending: boolean;
  activeFilterCount: number;
  nearMeArea?: AreaResponse | null;
  openAllFilters: (options: OpenAllFiltersOptions) => void;
  closeAllFilters: () => void;
  markUserInteraction: () => void;
};

type SearchFiltersProviderProps = {
  children: ReactNode;
  mode: ListingSearchMode;
  nearMeArea?: AreaResponse | null;
  seedFromRecentSearch?: boolean;
};

const SearchFiltersContext = createContext<SearchFiltersContextValue | null>(null);

export function SearchFiltersProvider({
  children,
  mode,
  nearMeArea,
  seedFromRecentSearch,
}: SearchFiltersProviderProps) {
  const {
    filters,
    setFilters,
    applyFilters,
    area,
    setArea,
    submit,
    searchArea,
    isPending,
    activeFilterCount,
    markUserInteraction,
  } = useListingSearchState({
    mode,
    nearMeArea,
    seedFromRecentSearch,
  });

  // The All-Filters modal is hosted by the persistent search shell when present
  // (SRP/HDP) so it survives the slug morph that remounts the page-level search bar.
  // Without a shell (e.g. the homepage hero), fall back to a local modal.
  const shellModal = useAllFiltersModal();
  const [localOpen, setLocalOpen] = useState(false);
  const [localHasOpened, setLocalHasOpened] = useState(false);
  const [localOptions, setLocalOptions] = useState<OpenAllFiltersOptions>(defaultModalOptions);

  const openAllFilters = useCallback(
    async (options: OpenAllFiltersOptions) => {
      markUserInteraction();
      if (shellModal) {
        shellModal.openAllFilters(options);
        return;
      }
      await interactionResponse();
      setLocalOptions(options);
      setLocalHasOpened(true);
      setLocalOpen(true);
    },
    [markUserInteraction, shellModal],
  );

  const closeAllFilters = useCallback(() => {
    if (shellModal) {
      shellModal.closeAllFilters();
      return;
    }
    setLocalOpen(false);
  }, [shellModal]);

  const value = useMemo(
    () => ({
      filters,
      setFilters,
      applyFilters,
      area,
      setArea,
      submit,
      searchArea,
      isPending,
      activeFilterCount,
      nearMeArea,
      openAllFilters,
      closeAllFilters,
      markUserInteraction,
    }),
    [
      filters,
      setFilters,
      applyFilters,
      area,
      setArea,
      submit,
      searchArea,
      isPending,
      activeFilterCount,
      nearMeArea,
      openAllFilters,
      closeAllFilters,
      markUserInteraction,
    ],
  );

  return (
    <SearchFiltersContext.Provider value={value}>
      {children}
      {!shellModal && localHasOpened && (
        <AllFiltersModal
          isOpen={localOpen}
          onClose={closeAllFilters}
          filters={filters}
          setFilters={setFilters}
          showRoomsSection={localOptions.showRoomsSection}
          showPriceSection={localOptions.showPriceSection}
        />
      )}
    </SearchFiltersContext.Provider>
  );
}

export function useSearchFilters(): SearchFiltersContextValue {
  const context = useContext(SearchFiltersContext);

  if (!context) {
    throw new Error('useSearchFilters must be used within a SearchFiltersProvider');
  }

  return context;
}

export type { OpenAllFiltersOptions } from './AllFiltersModalProvider';
